// ---------------------------------------------------------------------------
// DSS-3: Image background controls — position/size/repeat/parallax
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Image } from "lucide-react";

// ── Types ──
export type BgSize = "cover" | "contain" | "auto" | "custom";
export type BgRepeat = "no-repeat" | "repeat" | "repeat-x" | "repeat-y";
export type BgAttachment = "scroll" | "fixed";

export interface ImageBgConfig {
  readonly url: string;
  readonly positionX: number; // 0-100
  readonly positionY: number; // 0-100
  readonly size: BgSize;
  readonly customWidth: string; // e.g. "200px" or "50%"
  readonly customHeight: string;
  readonly repeat: BgRepeat;
  readonly attachment: BgAttachment;
}

// ── Constants ──
const SIZE_OPTIONS: readonly BgSize[] = ["cover", "contain", "auto", "custom"];
const REPEAT_OPTIONS: readonly BgRepeat[] = ["no-repeat", "repeat", "repeat-x", "repeat-y"];
const ATTACHMENT_LABELS: Record<BgAttachment, string> = { scroll: "Scroll", fixed: "Fixed (Parallax)" };

const GRID_9: readonly [number, number, string][] = [
  [0, 0, "TL"], [50, 0, "TC"], [100, 0, "TR"],
  [0, 50, "CL"], [50, 50, "C"], [100, 50, "CR"],
  [0, 100, "BL"], [50, 100, "BC"], [100, 100, "BR"],
];

const DEFAULT_IMAGE_BG: ImageBgConfig = {
  url: "",
  positionX: 50,
  positionY: 50,
  size: "cover",
  customWidth: "100%",
  customHeight: "100%",
  repeat: "no-repeat",
  attachment: "scroll",
};

// ── NaN guard ──
function safeNum(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

// ── CSS Generator ──
export function generateImageBgCss(config: ImageBgConfig): string {
  if (!config.url) return "";

  const px = safeNum(config.positionX, 0, 100);
  const py = safeNum(config.positionY, 0, 100);

  const sizeVal =
    config.size === "custom"
      ? `${config.customWidth || "auto"} ${config.customHeight || "auto"}`
      : config.size;

  return [
    `background-image: url(${config.url})`,
    `background-position: ${px}% ${py}%`,
    `background-size: ${sizeVal}`,
    `background-repeat: ${config.repeat}`,
    `background-attachment: ${config.attachment}`,
  ].join("; ");
}

// ── Props ──
interface ImageBgControlsProps {
  readonly value: ImageBgConfig;
  readonly onChange: (config: ImageBgConfig) => void;
}

// ── Component ──
export default function ImageBgControls({ value, onChange }: ImageBgControlsProps) {
  const uid = useId();
  const config = value ?? DEFAULT_IMAGE_BG;

  const handleUrl = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...config, url: e.target.value }),
    [config, onChange],
  );

  const handlePositionGrid = useCallback(
    (x: number, y: number) => onChange({ ...config, positionX: x, positionY: y }),
    [config, onChange],
  );

  const handlePositionX = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...config, positionX: safeNum(Number(e.target.value), 0, 100) });
    },
    [config, onChange],
  );

  const handlePositionY = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...config, positionY: safeNum(Number(e.target.value), 0, 100) });
    },
    [config, onChange],
  );

  const handleSize = useCallback(
    (size: BgSize) => onChange({ ...config, size }),
    [config, onChange],
  );

  const handleCustomWidth = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...config, customWidth: e.target.value }),
    [config, onChange],
  );

  const handleCustomHeight = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...config, customHeight: e.target.value }),
    [config, onChange],
  );

  const handleRepeat = useCallback(
    (repeat: BgRepeat) => onChange({ ...config, repeat }),
    [config, onChange],
  );

  const handleAttachment = useCallback(
    (attachment: BgAttachment) => onChange({ ...config, attachment }),
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* URL input */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${uid}-url`}
          className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          Image URL
        </label>
        <div className="flex items-center gap-1.5">
          <Image className="h-3.5 w-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
          <input
            id={`${uid}-url`}
            type="text"
            value={config.url}
            onChange={handleUrl}
            placeholder="https://..."
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-[10px] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
          />
        </div>
      </div>

      {/* Position — 9-point grid + custom */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Position</span>
        <div className="flex items-start gap-3">
          <div className="grid grid-cols-3 gap-1">
            {GRID_9.map(([x, y, label]) => {
              const isActive = x === config.positionX && y === config.positionY;
              return (
                <button key={label} type="button" title={label} onClick={() => handlePositionGrid(x, y)}
                  className={["h-4 w-4 rounded-sm border transition-colors", isActive
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]"
                    : "border-[var(--color-border)] bg-[var(--color-muted)] hover:border-[var(--color-primary)]/50",
                  ].join(" ")} />
              );
            })}
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label htmlFor={`${uid}-px`} className="w-4 text-[10px] text-[var(--color-muted-foreground)]">X</label>
              <input
                id={`${uid}-px`}
                type="number"
                min={0}
                max={100}
                value={config.positionX}
                onChange={handlePositionX}
                className="w-12 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--color-foreground)]"
              />
              <span className="text-[10px] text-[var(--color-muted-foreground)]">%</span>
            </div>
            <div className="flex items-center gap-1">
              <label htmlFor={`${uid}-py`} className="w-4 text-[10px] text-[var(--color-muted-foreground)]">Y</label>
              <input
                id={`${uid}-py`}
                type="number"
                min={0}
                max={100}
                value={config.positionY}
                onChange={handlePositionY}
                className="w-12 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--color-foreground)]"
              />
              <span className="text-[10px] text-[var(--color-muted-foreground)]">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Size */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Size</span>
        <div className="flex gap-1">
          {SIZE_OPTIONS.map((s) => (
            <button key={s} type="button" onClick={() => handleSize(s)}
              className={["flex-1 rounded px-1.5 py-1 text-[10px] font-medium capitalize transition-colors", config.size === s
                ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
              ].join(" ")}>
              {s}
            </button>
          ))}
        </div>
        {config.size === "custom" && (
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <label htmlFor={`${uid}-cw`} className="text-[10px] text-[var(--color-muted-foreground)]">W</label>
              <input
                id={`${uid}-cw`}
                type="text"
                value={config.customWidth}
                onChange={handleCustomWidth}
                placeholder="100%"
                className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1.5 py-0.5 text-[10px] text-[var(--color-foreground)]"
              />
            </div>
            <div className="flex items-center gap-1">
              <label htmlFor={`${uid}-ch`} className="text-[10px] text-[var(--color-muted-foreground)]">H</label>
              <input
                id={`${uid}-ch`}
                type="text"
                value={config.customHeight}
                onChange={handleCustomHeight}
                placeholder="100%"
                className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1.5 py-0.5 text-[10px] text-[var(--color-foreground)]"
              />
            </div>
          </div>
        )}
      </div>

      {/* Repeat */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Repeat</span>
        <div className="flex gap-1">
          {REPEAT_OPTIONS.map((r) => (
            <button key={r} type="button" onClick={() => handleRepeat(r)}
              className={["flex-1 rounded px-1 py-1 text-[10px] font-medium transition-colors", config.repeat === r
                ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
              ].join(" ")}>
              {r === "no-repeat" ? "None" : r === "repeat" ? "Both" : r === "repeat-x" ? "X" : "Y"}
            </button>
          ))}
        </div>
      </div>

      {/* Attachment */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Attachment</span>
        <div className="flex gap-1">
          {(["scroll", "fixed"] as const).map((a) => (
            <button key={a} type="button" onClick={() => handleAttachment(a)}
              className={["flex-1 rounded px-2 py-1 text-[10px] font-medium transition-colors", config.attachment === a
                ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
              ].join(" ")}>
              {ATTACHMENT_LABELS[a]}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      {config.url && (
        <div
          className="h-16 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]"
          style={{
            backgroundImage: `url(${config.url})`,
            backgroundPosition: `${safeNum(config.positionX, 0, 100)}% ${safeNum(config.positionY, 0, 100)}%`,
            backgroundSize: config.size === "custom"
              ? `${config.customWidth || "auto"} ${config.customHeight || "auto"}`
              : config.size,
            backgroundRepeat: config.repeat,
          }}
        />
      )}
    </div>
  );
}

export { DEFAULT_IMAGE_BG };
